/**
 * GridLayout.ts - GridLayout - Simple grid positioning
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
/**
 * Calculate grid layout positions
 * 
 * @param {Array} nodes - Array of node data
 * @param {Array} edges - Array of edge data (ignored for grid layout)
 * @param {Object} options - Layout options
 * @param {number|string} options.columns - Number of columns or 'auto'
 * @param {number|string} options.rowHeight - Height of each row or 'auto'
 * @param {number|string} options.columnWidth - Width of each column or 'auto'
 * @param {number} options.gap - Gap between items (default: 16)
 * @param {boolean} options.showHeaders - Whether to show headers (default: false)
 * @param {string} options.containerWidth - Container width for auto calculations
 * @param {string} options.containerHeight - Container height for auto calculations
 * 
 * @returns {Object} Layout result with positioned nodes
 */
export function calculateGridLayout(nodes, edges = [], options = {}) {
  const {
    columns = 4,
    rowHeight = 100,
    columnWidth = 100,
    gap = 16,
    showHeaders = false,
    containerWidth,
    containerHeight
  } = options;
  
  // Auto-detect columns from data if 'auto'
  const cols = columns === 'auto' 
    ? detectColumnsFromData(nodes)
    : columns;
  
  // Calculate actual dimensions
  const actualColumnWidth = columnWidth === 'auto' && containerWidth
    ? (containerWidth - (gap * (cols - 1))) / cols
    : columnWidth;
    
  const actualRowHeight = rowHeight === 'auto'
    ? 100 // Default height
    : rowHeight;
  
  // Calculate positions
  const positions = nodes.map((node, index) => {
    const row = Math.floor(index / cols);
    const col = index % cols;
    
    return {
      ...node,
      x: col * (actualColumnWidth + gap),
      y: row * (actualRowHeight + gap),
      width: actualColumnWidth,
      height: actualRowHeight
    };
  });
  
  // Calculate total dimensions
  const rows = Math.ceil(nodes.length / cols);
  const totalWidth = cols * actualColumnWidth + (cols - 1) * gap;
  const totalHeight = rows * actualRowHeight + (rows - 1) * gap;
  
  return {
    nodes: positions,
    edges: [], // Grid layout doesn't use edges
    metadata: {
      layout: 'grid',
      columns: cols,
      rows,
      totalWidth,
      totalHeight,
      showHeaders
    }
  };
}

/**
 * Detect number of columns from data structure
 * For objects, use number of properties
 * For arrays, use reasonable default
 */
function detectColumnsFromData(nodes) {
  if (nodes.length === 0) return 4;
  
  // If nodes are objects with properties, could use that
  const firstNode = nodes[0];
  if (typeof firstNode === 'object' && firstNode !== null) {
    const keys = Object.keys(firstNode).filter(k => k !== 'id');
    if (keys.length > 0 && keys.length <= 12) {
      return keys.length;
    }
  }
  
  // Default based on number of items
  if (nodes.length <= 4) return nodes.length;
  if (nodes.length <= 9) return 3;
  if (nodes.length <= 16) return 4;
  return 5;
}

/**
 * Export for use in Chart component
 */
export default {
  calculate: calculateGridLayout,
  name: 'grid',
  displayName: 'Simple Grid'
};
